import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/auth';
import { useUi } from '../../store/ui';
import { useGet } from '../../api/hooks';
import type { SystemInfo } from '../../api/types';
import { usePhone } from '../../lib/useMediaQuery';
import { watchStackTables } from '../../lib/stackTables';
import { NAV, pageTitleFor, phoneTabs, TAB_LABEL } from '../../app/nav';
import { DashboardDateFilter } from '../../features/dashboard/DateFilter';
import { Icon, Loading, Toasts } from '../ui';
import { ProfileMenu } from './ProfileMenu';
import { ErrorBoundary } from './ErrorBoundary';
import { NotificationsDrawer } from './NotificationsDrawer';
import { useIdleLogout } from './useIdleLogout';

/**
 * App shell. From 768px up: a fixed-height flex row, collapsible sidebar on the left and a work column that scrolls
 * on its own. Below 768px: the sidebar is a slide-in drawer (hamburger or the More tab), the top bar shows the page
 * title, and a bottom tab bar holds the four most useful destinations.
 */
export function Shell() {
  const user = useAuth((s) => s.user)!;
  const can = useAuth((s) => s.can);
  const { navOpen, setNavOpen, navCollapsed, toggleNavCollapsed, toggleTheme } = useUi();
  const nav = useNavigate();
  const loc = useLocation();
  const phone = usePhone();
  const [drawer, setDrawer] = useState(false);
  const [q, setQ] = useState('');
  const rail = useRef<HTMLElement>(null);
  const main = useRef<HTMLDivElement>(null);
  useIdleLogout();

  const info = useGet<SystemInfo>('/system/info');
  const waiting = useGet<{ count: number }>(can('approval.act') ? '/approvals/count' : null, undefined, { refetchInterval: 60_000 });
  const unread = useGet<{ count: number }>('/notifications/unread-count', undefined, { refetchInterval: 60_000 });

  const groups = NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.perms.length || can(...i.perms)) })).filter((g) => g.items.length);
  const tabs = phoneTabs(groups.flatMap((g) => g.items));
  const collapsed = navCollapsed && !phone;

  // the work column scrolls on its own, so a new page starts at its top
  useEffect(() => {
    main.current?.scrollTo?.(0, 0);
    setNavOpen(false);
  }, [loc.pathname, setNavOpen]);

  useEffect(() => watchStackTables(), []);

  // a closed drawer must not be reachable by keyboard or screen reader; an open one locks the page behind it
  useEffect(() => {
    if (rail.current) rail.current.inert = phone && !navOpen;
  }, [phone, navOpen]);
  useEffect(() => {
    if (!phone || !navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', esc);
    };
  }, [phone, navOpen, setNavOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim().length >= 2) {
      setNavOpen(false);
      nav(`/search?q=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <div className={`app ${navOpen ? 'nav-open' : ''} ${collapsed ? 'rail-collapsed' : ''}`}>
      <a className="skip-link" href="#main">Skip to content</a>
      <aside className="rail" aria-label="Main navigation" ref={rail}>
        <div className="brand">
          <div className="brand-mark"><img src="/logo.png" alt="MHS logo" /></div>
          <div className="brand-text">
            <b>MHS Finance</b>
            <small>Management System</small>
          </div>
          <button className="icon-btn rail-toggle" type="button" aria-label={navCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded={!navCollapsed} onClick={toggleNavCollapsed}>
            <Icon name="back" style={{ transform: navCollapsed ? 'rotate(180deg)' : undefined }} />
          </button>
          <button className="icon-btn rail-close" type="button" aria-label="Close menu" onClick={() => setNavOpen(false)}>
            <Icon name="x" />
          </button>
        </div>
        <form className="rail-search" onSubmit={submitSearch} role="search">
          <Icon name="search" />
          <input name="rail-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PR, PO, invoice…" aria-label="Search" enterKeyHint="search" />
        </form>
        {groups.map((g) => (
          <div className="nav-group" key={g.group}>
            <span>{g.group}</span>
            <nav className="nav" aria-label={g.group}>
              {g.items.map((i) => {
                const count = i.id === 'approvals' && waiting.data?.count ? waiting.data.count : 0;
                return (
                  <NavLink key={i.id} data-nav={i.id} to={i.path} className={({ isActive }) => (isActive ? 'active' : '')} title={collapsed ? i.label : undefined} aria-label={collapsed ? `${i.label}${count ? `, ${count} waiting` : ''}` : undefined} onClick={() => setNavOpen(false)}>
                    <Icon name={i.icon} />
                    <span className="nav-label">{i.label}</span>
                    {count ? <span className="count">{count}</span> : null}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        ))}
        <div className="rail-foot">
          <button className="btn rail-theme" type="button" onClick={toggleTheme}>
            <Icon name="sun" />Switch light or dark theme
          </button>
          <span className="rail-foot-text">
            {info.data?.financialYear?.label ?? ''} · {info.data?.org.name ?? 'MHS'}
            <br />
            {user.scope === 'OWN_DEPT' && user.department ? `${user.department.name} scope` : ''}
          </span>
        </div>
      </aside>
      <div className="rail-scrim" onClick={() => setNavOpen(false)} />
      <div className="main" ref={main}>
        <header className="topbar">
          <button className="icon-btn menu-btn" aria-label="Open menu" aria-expanded={navOpen} onClick={() => setNavOpen(true)}>
            <Icon name="menu" />
          </button>
          <span className="top-title">{pageTitleFor(loc.pathname)}</span>
          <form className="search" onSubmit={submitSearch} role="search">
            <span><Icon name="search" /></span>
            <input id="q" name="q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PR, PO, invoice, vendor, UTR…" aria-label="Search" />
          </form>
          <div className="top-right">
            {loc.pathname === '/dashboard' ? <DashboardDateFilter className="in-top" /> : null}
            {info.data?.period ? (
              <span className="period" title="Current period">
                {info.data.period.name} · {info.data.period.status.toLowerCase()}
              </span>
            ) : null}
            <button className="icon-btn theme-btn" aria-label="Switch light or dark theme" onClick={toggleTheme}>
              <Icon name="sun" />
            </button>
            <button className="icon-btn" aria-label={`Notifications${unread.data?.count ? `, ${unread.data.count} unread` : ''}`} onClick={() => setDrawer(true)}>
              <Icon name="bell" />
              {unread.data?.count ? <span className="dot">{unread.data.count > 99 ? '99+' : unread.data.count}</span> : null}
            </button>
            <ProfileMenu />
          </div>
        </header>
        <main className="content" id="main">
          {user.readOnly ? (
            <div className="readonly-banner">
              Read-only access
              {user.assignedPeriod ? ` · limited to ${user.assignedPeriod.from} to ${user.assignedPeriod.to}` : ''}
              {user.accessExpiresAt ? ` · access expires ${new Date(user.accessExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
              . Every page you open is logged.
            </div>
          ) : null}
          <ErrorBoundary resetKey={loc.pathname}>
            <Suspense key={loc.pathname.split('/')[1]} fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <nav className="tabbar" aria-label="Primary">
        {tabs.map((i) => (
          <NavLink key={i.id} to={i.path} className={({ isActive }) => (isActive ? 'active' : '')}>
            <Icon name={i.icon} />
            <span>{TAB_LABEL[i.id] ?? i.label}</span>
            {i.id === 'approvals' && waiting.data?.count ? <span className="count">{waiting.data.count}</span> : null}
          </NavLink>
        ))}
        <button type="button" className={navOpen ? 'active' : ''} aria-expanded={navOpen} onClick={() => setNavOpen(true)}>
          <Icon name="menu" />
          <span>More</span>
        </button>
      </nav>
      {drawer ? <NotificationsDrawer onClose={() => setDrawer(false)} /> : null}
      <Toasts />
    </div>
  );
}
